// Type definitions for the LTI 1.3 Integrations feature.
// Mirrors the SDK's ILtiPlatform / ILtiResourceLink and the admin DTOs
// (RegisterLtiPlatformDto, MapResourceLinkDto) exposed under /lti/admin/*.

export enum LtiPlatformStatus {
  ACTIVE = 'ACTIVE',
  SUSPENDED = 'SUSPENDED',
}

export enum LtiResourceType {
  COURSE = 'COURSE',
  CERTIFICATION = 'CERTIFICATION',
  LEARNING_PATH = 'LEARNING_PATH',
}

/** A registered LMS platform allowed to launch Skillzen. */
export interface LtiPlatform {
  _id: string;
  tenant_key: string;
  issuer: string;
  client_id: string;
  authorization_endpoint: string;
  token_endpoint: string;
  jwks_url: string;
  audience?: string;
  ags_enabled: boolean;
  nrps_enabled: boolean;
  deep_linking_enabled: boolean;
  allowed_iframe_origins?: string[];
  status: LtiPlatformStatus;
  createdAt: string;
  updatedAt: string;
}

/** Payload for registering / updating a platform (RegisterLtiPlatformDto). */
export interface RegisterLtiPlatformRequest {
  issuer: string;
  client_id: string;
  authorization_endpoint: string;
  token_endpoint: string;
  jwks_url: string;
  audience?: string;
  deployment_ids?: string[];
  ags_enabled?: boolean;
  nrps_enabled?: boolean;
  deep_linking_enabled?: boolean;
  allowed_iframe_origins?: string[];
}

/** A mapping from an LMS resource link to a Skillzen resource. */
export interface LtiResourceLink {
  _id: string;
  tenant_key: string;
  issuer: string;
  resource_link_id: string;
  skillzen_resource_type: LtiResourceType;
  skillzen_resource_id: string;
  ags_lineitem_url?: string;
  ags_scopes?: string[];
  nrps_memberships_url?: string;
  nrps_last_synced_at?: string;
  context_id?: string;
  /** LMS names, refreshed on every launch. Absent until the link is launched. */
  context_title?: string;
  resource_link_title?: string;
  created_via: 'deep_linking' | 'manual' | 'launch';
  /** Name of the Skillzen content, resolved server-side. */
  skillzen_title?: string;
  /** False when that content has been deleted — the link now opens nothing. */
  skillzen_exists?: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Payload for mapping a resource link (MapResourceLinkDto). */
export interface MapResourceLinkRequest {
  issuer: string;
  resource_link_id: string;
  skillzen_resource_type: LtiResourceType;
  skillzen_resource_id: string;
}

/** A single RSA JWK in our published key set. */
export interface JwkRsa {
  kty: 'RSA';
  n: string;
  e: string;
  kid?: string;
  alg?: string;
  use?: 'sig' | 'enc';
  key_ops?: string[];
}

/** The tenant's published JWK Set (GET /lti/admin/jwks). */
export interface JwkSet {
  keys: JwkRsa[];
}

// --- API response envelopes (TypedResultEntity: { success, code, data, message }) ---

export interface LtiPlatformResponse {
  success: boolean;
  data: LtiPlatform;
}

export interface LtiPlatformsListResponse {
  success: boolean;
  data: LtiPlatform[];
}

export interface LtiResourceLinkResponse {
  success: boolean;
  data: LtiResourceLink;
}

export interface JwkSetResponse {
  success: boolean;
  data: JwkSet;
}

// ---------------------------------------------------------------------------
// Deep Linking — the teacher picking Skillzen content from inside their LMS
// ---------------------------------------------------------------------------

/** A Skillzen resource offered in the content picker. */
export interface LtiSelectableResource {
  resource_type: LtiResourceType;
  resource_id: string;
  title: string;
  description?: string;
  image_url?: string;
  /** Ask the LMS to create a gradebook column. Defaults to true server-side. */
  gradable?: boolean;
}

/**
 * Where to send the signed selection, and the JWT to send.
 *
 * The browser must FORM-POST this to return_url — the LMS is a different
 * origin and needs a real navigation, not a fetch.
 */
export interface LtiDeepLinkingCompletion {
  return_url: string;
  jwt: string;
}

export interface LtiDeepLinkingCompletionResponse {
  success: boolean;
  data: LtiDeepLinkingCompletion;
}


// ---------------------------------------------------------------------------
// Links launched from an LMS but not yet mapped to Skillzen content
// ---------------------------------------------------------------------------

/**
 * A link an LMS has launched that nobody has mapped.
 *
 * The LMS identifies an activity by an internal id it never displays, so the
 * titles are what an administrator actually recognises — the id is carried
 * along so the mapping can be saved without anyone having to find it.
 */
export interface LtiUnmappedLink {
  _id: string;
  issuer: string;
  resource_link_id: string;
  resource_link_title?: string;
  context_id?: string;
  context_title?: string;
  deployment_id?: string;
  launch_count: number;
  first_seen_at: string;
  last_seen_at: string;
}

export interface LtiUnmappedLinksResponse {
  success: boolean;
  data: LtiUnmappedLink[];
}

/** One page of mapped resource links. */
export interface LtiResourceLinkList {
  items: LtiResourceLink[];
  total: number;
}

/** Whether LTI is switched on for this tenant, and whether its key exists. */
export interface LtiTenantStatus {
  enabled: boolean;
  /** Every LTI signing key is sealed under this; without it a launch cannot complete. */
  has_master_key: boolean;
  master_key_version?: number;
  master_key_created_at?: string;
}
